import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const AdminAuthContext = createContext();

export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const refreshToken = localStorage.getItem('adminRefreshToken');
    
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expiré - l'intercepteur axios gérera le refresh automatiquement
          if (refreshToken && decoded.role === 'admin') {
            // On a un refresh token, on peut tenter de se reconnecter
            setIsAdminAuthenticated(true);
            setCurrentAdmin(decoded);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          } else {
            // Pas de refresh token, déconnecter
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminRefreshToken');
            setIsAdminAuthenticated(false);
            setCurrentAdmin(null);
          }
        } else if (decoded.role === 'admin') {
          // Token valide et rôle admin
          setIsAdminAuthenticated(true);
          setCurrentAdmin(decoded);
          
          // Définir le header d'authentification pour toutes les requêtes admin
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          // Token valide mais pas admin
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminRefreshToken');
          setIsAdminAuthenticated(false);
          setCurrentAdmin(null);
        }
      } catch (error) {
        // Token invalide
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        setIsAdminAuthenticated(false);
        setCurrentAdmin(null);
      }
    }
    setLoading(false);
  }, []);

  const loginAdmin = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/admin/login`, {
        email,
        password
      });
      
      const { accessToken, refreshToken, admin } = response.data;
      localStorage.setItem('adminToken', accessToken);
      localStorage.setItem('adminRefreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setCurrentAdmin(admin);
      setIsAdminAuthenticated(true);
      return admin;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la connexion';
      setError(errorMessage);
      throw error;
    }
  };

  const logoutAdmin = async () => {
    try {
      const refreshToken = localStorage.getItem('adminRefreshToken');
      if (refreshToken) {
        // Appeler l'API pour supprimer le refresh token
        await axios.post(`${API_URL}/auth/logout`, { refreshToken });
      }
    } catch (error) {
      console.error('Error during admin logout:', error);
      // Continuer même en cas d'erreur
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRefreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentAdmin(null);
      setIsAdminAuthenticated(false);
    }
  };

  /**
   * Request a password reset email
   */
  const requestPasswordReset = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la demande de réinitialisation';
      setError(errorMessage);
      throw error;
    }
  };

  const value = {
    currentAdmin,
    isAdminAuthenticated,
    loading,
    error,
    loginAdmin,
    logoutAdmin,
    requestPasswordReset
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
